import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import { toast } from 'sonner'

import { authors, users, type AdminUser } from '../api/endpoints'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { FormField } from '../components/FormField'
import { Input, Textarea } from '../components/Input'
import { ImageUploader } from '../components/ImageUploader'
import { PageLoader } from '../components/Spinner'
import { RoleBadge } from '../components/StatusBadge'
import { toFormData } from '../lib/formData'

/** Edit a user's author profile (bio, public slug, public photo).
 *  Reachable from /users (per-row link) and from BlogPostEditor's
 *  Author sidebar. Anyone with admin or author role can have one. */
export function AuthorProfilePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [user, setUser] = useState<AdminUser | null>(null)
  const [bio, setBio] = useState('')
  const [authorSlug, setAuthorSlug] = useState('')
  const [authorAvatar, setAuthorAvatar] = useState('')
  /* See BlogPostEditor's note for the cover-image triplet pattern. */
  const [avatarFile, setAvatarFile] = useState<Blob | string | null>(null)
  const [avatarCleared, setAvatarCleared] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    users
      .detail(Number(id))
      .then((r) => {
        if (cancelled) return
        const u = r.user
        // AdminUserListItem uses avatarUrl; AdminUser uses avatar.
        // Map to the smaller AdminUser shape that the rest of this
        // page expects.
        setUser({
          id: u.id,
          name: u.name,
          email: u.email,
          phone: u.phone,
          role: u.role,
          avatar: u.avatarUrl,
          authorSlug: u.authorSlug,
          bio: u.bio,
          authorAvatar: u.authorAvatar,
        })
        setBio(u.bio ?? '')
        setAuthorSlug(u.authorSlug ?? '')
        setAuthorAvatar(u.authorAvatar ?? '')
      })
      .catch((err) => toast.error('Failed to load user', { description: err.message }))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  function autoSlug(s: string) {
    return s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 100)
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!user) return
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        bio: bio.trim() || null,
        authorSlug: authorSlug.trim() || null,
      }

      // Match BlogPostEditor's cover image pattern: FormData when a
      // new file is picked, JSON otherwise (with explicit null on
      // clear).
      if (avatarFile instanceof Blob) {
        const fd = toFormData(payload, { authorAvatar: avatarFile })
        await authors.updateProfile(user.id, fd)
      } else {
        await authors.updateProfile(user.id, {
          ...payload,
          ...(avatarCleared ? { authorAvatar: null } : {}),
        })
      }
      toast.success('Author profile saved')
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  if (loading || !user) return <PageLoader />

  return (
    <form onSubmit={onSubmit} className="space-y-5 max-w-4xl pb-24">
      <Link
        to="/users"
        className="text-sm text-slatey hover:text-ink inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="size-3.5" /> Back to users
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">
            {user.name}
          </h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-slatey">
            <span>{user.phone}</span>
            <RoleBadge role={user.role} />
          </div>
        </div>
        <Button onClick={onSubmit} loading={saving}>
          <Save className="size-4" />
          Save profile
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-5">
        <Card className="p-5 space-y-4">
          <h2 className="font-display text-base font-semibold text-ink">
            Author profile
          </h2>
          <FormField
            label="Bio"
            hint="Shown on the public author archive page (up to 2000 chars)."
          >
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={2000}
              className="min-h-[180px]"
              placeholder="A short bio of the author…"
            />
          </FormField>
          <FormField
            label="Author slug"
            hint="Public URL segment for the author's archive (e.g. /blog/author/dr-anjali)."
          >
            <Input
              value={authorSlug}
              onChange={(e) => setAuthorSlug(e.target.value)}
              placeholder={autoSlug(user.name)}
            />
          </FormField>
        </Card>

        <aside className="space-y-5 lg:sticky lg:top-6 lg:self-start">
          <Card className="p-5 space-y-4">
            <h2 className="font-display text-base font-semibold text-ink">
              Public photo
            </h2>
            <FormField
              label="Author avatar"
              hint="Used on the public blog in lieu of the user's account avatar."
            >
              <ImageUploader
                value={authorAvatar || null}
                onChange={(next) => {
                  if (next instanceof Blob) {
                    setAvatarFile(next)
                    setAvatarCleared(false)
                  } else if (typeof next === 'string') {
                    setAuthorAvatar(next)
                    setAvatarFile(null)
                    setAvatarCleared(false)
                  } else {
                    setAuthorAvatar('')
                    setAvatarFile(null)
                    setAvatarCleared(true)
                  }
                }}
                label="Author photo"
              />
            </FormField>
          </Card>

          <Card className="p-5 space-y-2">
            <h2 className="font-display text-base font-semibold text-ink">
              Promote to author
            </h2>
            <p className="text-xs text-slatey">
              To allow this user to log in and manage their own posts,
              change their role to <span className="font-medium">author</span>{' '}
              on the user edit page.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/users/${user.id}`)}
            >
              Open user record
            </Button>
          </Card>
        </aside>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-line bg-white/90 backdrop-blur px-6 py-3 flex items-center justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/users')}>
          Cancel
        </Button>
        <Button onClick={onSubmit} loading={saving}>
          <Save className="size-4" />
          Save profile
        </Button>
      </div>
    </form>
  )
}